import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { SearchIcon } from "@chakra-ui/icons";
import JobList from "./JobList";
import JobViewComponent from "./JobViewComponent";
import { Header } from "build-ts/metadata";
import { ScenarioAnalysis } from "build-ts/analyst";
import { listScheduledJobs } from "services/ScheduleServices";

interface AnalystJobsComponentProps {
  analystId: string | undefined;
  scenarios: ScenarioAnalysis[] | undefined;
  isTabActive?: number;
  setIsViewingOrEditingJob?: (value: boolean) => void;
  analystJobsMap: Map<string, Header[]>;
  setAnalystJobsMap: React.Dispatch<React.SetStateAction<Map<string, Header[]>>>;
}

const AnalystJobsComponent: React.FC<AnalystJobsComponentProps> = ({
  analystId,
  scenarios,
  isTabActive = 0,
  setIsViewingOrEditingJob,
  analystJobsMap,
  setAnalystJobsMap,
}) => {
  const [isCreatingOrViewingJob, setIsCreatingOrViewingJob] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jobs, setJobs] = useState<Header[]>([]);
  const [selectedJob, setSelectedJob] = useState<Header | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter jobs based on search term
  const filteredJobs = useMemo(() => {
    if (!searchTerm.trim()) {
      return jobs;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return jobs.filter((job) => {
      return job.name?.toLowerCase().includes(lowerSearchTerm);
    });
  }, [jobs, searchTerm]);

  const getJobs = async (analystId: string) => {
    setIsLoading(true);
    if (analystId) {
      const response = await listScheduledJobs(
        "ANALYST_JOB",
        -1,
        1,
        analystId,
        "LAST_MODIFIED",
        false,
        true, /* includeScheduleActivity */
        "", /* activeSchedule */
      );
      if (response) {
        setJobs(response);
        // Update the map with the new jobs list
        setAnalystJobsMap(prevMap => {
          const newMap = new Map(prevMap);
          newMap.set(analystId, response);
          return newMap;
        });
      }
    } else {
      setJobs([]);
    }
    setIsLoading(false);
  };

  // Initialize jobs from the map instead of fetching
  useEffect(() => {
    if (analystId && analystJobsMap.has(analystId)) {
      const jobsFromMap = analystJobsMap.get(analystId);
      if (jobsFromMap) {
        setJobs(jobsFromMap);
      } else {
        setJobs([]);
      }
    }
  }, [analystId, analystJobsMap]);

  const handleCreateNewJob = () => {
    setSelectedJob(undefined);
    setIsCreatingOrViewingJob(true);
    setIsViewingOrEditingJob?.(true); // Notify parent
  };

  const handleJobClick = (job: Header) => {
    setSelectedJob(job);
    setIsCreatingOrViewingJob(true);
    setIsViewingOrEditingJob?.(true); // Notify parent
  };

  const handleBackToList = () => {
    setIsCreatingOrViewingJob(false);
    setSelectedJob(undefined);
    setIsViewingOrEditingJob?.(false); // Notify parent
    // Refetch jobs to get updated data
    if (analystId) {
      getJobs(analystId);
    }
  };

  const handleRefresh = () => {
    if (analystId) {
      getJobs(analystId);
    }
  };

  if (isCreatingOrViewingJob) {
    return (
      <JobViewComponent
        onBack={handleBackToList}
        scenarios={scenarios}
        isCreatingNew={!selectedJob}
        selectedJob={selectedJob}
        isJobsTabActive={isTabActive === 3}
      />
    );
  }

  return (
    <Box height="100%" bg="white" p={4}>
      {/* Header with Job Count, Search Bar, and Create Button */}
      <HStack justify="space-between" align="center" mb={4} spacing={4}>
        {/* Job Count */}
        <Text fontSize="lg" fontWeight="600" color="fg.default" m={0} minW="150px">
          Showing {filteredJobs.length} {filteredJobs.length === 1 ? "Job" : "Jobs"}
        </Text>

        {/* Search Bar */}
        <InputGroup size="md" maxW="500px" flex="1">
          <InputLeftElement pointerEvents="none" height="full">
            <SearchIcon color="gray.400" boxSize={4} />
          </InputLeftElement>
          <Input
            placeholder="Search jobs by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="gray.50"
            border="1px"
            borderColor="gray.200"
            fontSize="sm"
            _hover={{
              borderColor: "gray.300",
              bg: "white"
            }}
            _focus={{
              borderColor: "purple.400",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
              bg: "white"
            }}
            _placeholder={{
              color: "gray.400"
            }}
          />
        </InputGroup>

        {/* Create New Job Button */}
        <Button
          variant="outline"
          size="sm"
          fontWeight="600"
          borderWidth="2px"
          borderColor="purple.500"
          color="purple.500"
          fontSize="md"
          cursor="pointer"
          minW="160px"
          _hover={{
            bg: "purple.500",
            color: "white",
            fontWeight: "700",
          }}
          leftIcon={<Icon as={FaPlus} boxSize={4} />}
          onClick={handleCreateNewJob}
        >
          Create New Job
        </Button>
      </HStack>

      {/* Job List */}
      <JobList
        jobs={filteredJobs}
        isLoadingJobs={isLoading}
        onJobClick={handleJobClick}
        onRefresh={handleRefresh}
      />
    </Box>
  );
};

export default AnalystJobsComponent;

